import React, { useEffect, useRef } from 'react';
import { Box, ButtonBase, Chip, Collapse, Paper, Typography } from '@mui/material';
import { LedgerRow, Snapshot } from './lib/types';
import { ActionRow, gaIndex, ledgerSections } from './lib/selectors';
import { fmtIct } from './lib/ict';
import { ProvLine } from './chips';
import { FONT, OVERLINE, RADIUS, SHADOW, TONE } from './lib/brand';

function StatusChip({ status }: { status: string }) {
  const open = /^OPEN/i.test(status);
  const done = /^DONE/i.test(status);
  return (
    <Chip
      size="small"
      label={status}
      sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: open ? TONE.bad.bg : done ? TONE.good.bg : TONE.neutral.bg, color: open ? TONE.bad.fg : done ? TONE.good.fg : TONE.neutral.fg }}
    />
  );
}

function LedgerItem({
  row, expanded, onToggle, linked, onOpenAction,
}: {
  row: LedgerRow; expanded: boolean; onToggle: () => void; linked: ActionRow[]; onOpenAction: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (expanded && ref.current && typeof ref.current.scrollIntoView === 'function') ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [expanded]);
  const field = (k: string, v?: string | null) => (v ? (
    <Box sx={{ display: 'flex', gap: 1, fontSize: 12, py: 0.2 }}><Box sx={{ width: 62, color: 'text.secondary', flexShrink: 0 }}>{k}</Box><Box sx={{ minWidth: 0, wordBreak: 'break-word' }}>{v}</Box></Box>
  ) : null);
  return (
    <Box ref={ref} data-testid={`ledger-${row.id}`} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.5, py: 1, bgcolor: expanded ? 'action.hover' : 'transparent' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontFamily: FONT.mono, fontSize: 11.5, fontWeight: 700 }}>{row.id}</Typography>
          <Typography component="span" sx={{ fontSize: 12.5, flex: 1, minWidth: 0, textDecoration: row.struck ? 'line-through' : 'none' }}>{row.title}</Typography>
          <StatusChip status={row.status} />
        </Box>
        <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.3 }}>
          {[row.due ? `Due ${row.due}` : null, row.owner ? `Owner ${row.owner}` : null, row.linked ? `linked ${row.linked}` : null].filter(Boolean).join(' · ') || 'No due date or owner stated'}
        </Typography>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.2 }}>
          {field('Bucket', row.bucket)}
          {field('Due', row.due)}
          {field('Owner', row.owner)}
          {field('Linked', row.linked)}
          {field('Notes', row.notes)}
          <Box sx={{ mt: 0.5 }}><ProvLine prov={row.prov} fmt={fmtIct} /></Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 700 }}>Linked Action Center items ({linked.length})</Typography>
          {linked.length === 0
            ? <Typography variant="caption" color="text.secondary">None cite this row.</Typography>
            : linked.slice(0, 12).map(({ action, bot }) => (
              <ButtonBase key={action.id} onClick={() => onOpenAction(action.id)} sx={{ display: 'block', textAlign: 'left', width: '100%', py: 0.4, fontSize: 12, color: 'primary.main' }}>
                {bot.short} · {action.for || action.text}
              </ButtonBase>
            ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/** The MASTER-LEDGER panel: open critical security, Grant asks, run-sheet. Rows expand in place; ?ga= opens one. */
export function GrantAsksPanel({
  snapshot, expandedId, onToggle, onOpenAction,
}: {
  snapshot: Snapshot; expandedId: string | null; onToggle: (id: string) => void; onOpenAction: (id: string) => void;
}) {
  const sections = ledgerSections(snapshot.ledger);
  const idx = React.useMemo(() => gaIndex(snapshot), [snapshot]);
  const openGa = sections.asks.filter((r) => /^OPEN/i.test(r.status) && !r.struck).length;
  const openSec = sections.critical.filter((r) => /^OPEN/i.test(r.status) && !r.struck).length;
  const block = (title: string, rows: LedgerRow[]) => (rows.length ? (
    <Box key={title}>
      <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', px: 1.5, pt: 1.2, pb: 0.4, fontSize: 10.5 }}>{title}</Typography>
      {rows.map((r) => (
        <LedgerItem key={r.id} row={r} expanded={expandedId === r.id} onToggle={() => onToggle(r.id)} linked={idx.get(r.id) || []} onOpenAction={onOpenAction} />
      ))}
    </Box>
  ) : null);
  return (
    <Paper variant="outlined" component="aside" aria-label="Grant asks" data-testid="grant-asks" sx={{ borderRadius: RADIUS.xl, boxShadow: SHADOW.card, position: { lg: 'sticky' }, top: { lg: 80 }, maxHeight: { lg: 'calc(100vh - 100px)' }, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 1.5, pt: 1.4 }}>
        <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 15, fontWeight: 700 }}>Grant asks</Typography>
        <Typography variant="caption" color="text.secondary">{openGa} GA open · {openSec} critical SEC open</Typography>
      </Box>
      {block('Open critical security', sections.critical)}
      {block('Grant asks GA-01…', sections.asks)}
      {block('Run-sheet', sections.runsheet)}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5 }}>
        {snapshot.ledger_prov ? `${snapshot.ledger_prov.path} · ${fmtIct(snapshot.ledger_prov.mtime_ict)}. ` : ''}Only the CEO marks rows DONE after Grant confirms.
      </Typography>
    </Paper>
  );
}
